import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddService = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [serviceId, setServiceId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    image_url: '',
    website: '',
    email: '',
    telephone: '',
    price: '',
    subscription_price: '',
    facebook: '',
    instagram: '',
    twitter: '',
    telegram: ''
  });

  useEffect(() => {
    if (user?.subscription_plan !== 'tipster') {
      toast.error('Tipster subscription required');
      navigate('/subscription');
      return;
    }
    checkExistingService();
  }, [user]);

  const checkExistingService = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/tipster-services/my/service`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditing(true);
      setServiceId(response.data.id);
      setFormData({
        name: response.data.name,
        title: response.data.title,
        description: response.data.description,
        image_url: response.data.image_url || '',
        website: response.data.website || '',
        email: response.data.email,
        telephone: response.data.telephone || '',
        price: response.data.price || '',
        subscription_price: response.data.subscription_price || '',
        facebook: response.data.social_networks?.facebook || '',
        instagram: response.data.social_networks?.instagram || '',
        twitter: response.data.social_networks?.twitter || '',
        telegram: response.data.social_networks?.telegram || ''
      });
    } catch (error) {
      // No existing service, that's fine
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('session_token');
    
    const social_networks = {};
    if (formData.facebook) social_networks.facebook = formData.facebook;
    if (formData.instagram) social_networks.instagram = formData.instagram;
    if (formData.twitter) social_networks.twitter = formData.twitter;
    if (formData.telegram) social_networks.telegram = formData.telegram;

    const payload = {
      name: formData.name,
      title: formData.title,
      description: formData.description,
      image_url: formData.image_url || null,
      website: formData.website || null,
      email: formData.email,
      telephone: formData.telephone || null,
      price: formData.price || null,
      subscription_price: formData.subscription_price ? parseFloat(formData.subscription_price) : 0,
      social_networks
    };

    try {
      if (editing) {
        await axios.put(`${API}/tipster-services/${serviceId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service updated successfully!');
      } else {
        await axios.post(`${API}/tipster-services`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service created successfully!');
      }
      navigate('/tip-services');
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">{editing ? 'Edit' : 'Add'} Your Tip Service</h1>

          <Card className="glass-dark border-white/10">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white">Service Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="Your tipster name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="title" className="text-white">Title/Tagline *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="e.g., Professional Football Tips"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description" className="text-white">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="Describe your tipster service, your experience, success rate, etc."
                  />
                </div>

                <div>
                  <Label htmlFor="image_url" className="text-white">Image URL</Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="https://example.com/your-logo.jpg"
                  />
                  <p className="text-xs text-gray-400 mt-1">Upload your image to a hosting service and paste the URL</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-white">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="contact@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="telephone" className="text-white">Telephone (Optional)</Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="+1234567890"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="website" className="text-white">Website (Optional)</Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="https://yoursite.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="price" className="text-white">Price (Optional)</Label>
                    <Input
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="e.g., $49/month"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subscription_price" className="text-white">
                      Monthly Subscription Price (€)
                    </Label>
                    <Input
                      id="subscription_price"
                      name="subscription_price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.subscription_price}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="e.g., 29.99"
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      Set monthly subscription price. Users get access to ALL tips.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-white mb-3">Social Networks (Optional)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="facebook" className="text-white">Facebook</Label>
                      <Input
                        id="facebook"
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="instagram" className="text-white">Instagram</Label>
                      <Input
                        id="instagram"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="twitter" className="text-white">Twitter</Label>
                      <Input
                        id="twitter"
                        name="twitter"
                        value={formData.twitter}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="telegram" className="text-white">Telegram</Label>
                      <Input
                        id="telegram"
                        name="telegram"
                        value={formData.telegram}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        placeholder="https://t.me/..."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => navigate('/tip-services')}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white btn-hover"
                  >
                    {loading ? 'Saving...' : editing ? 'Update Service' : 'Create Service'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AddService;
